import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

type UseHashTabNavigationOptions<T extends string> = {
  /**
   * Map of tab values to their corresponding hash strings
   * Example: { TRADE: 'trade', MARKET_DATA: '' }
   */
  hashMap: Record<T, string>
  /**
   * Default tab to show when no hash is present
   */
  defaultTab: T
}

type UseHashTabNavigationReturn<T extends string> = {
  /**
   * Current active tab
   */
  activeTab: T
  /**
   * Navigate to a tab using hash navigation
   * - Creates a history entry (allows back button to work)
   */
  navigateToTab: (tab: T) => void
  /**
   * Switch to a tab without creating history entry
   * - Updates URL but doesn't add to browser history
   * - Useful for tab button clicks
   */
  switchToTab: (tab: T) => void
  /**
   * Manually set the active tab (doesn't update URL)
   */
  setActiveTab: (tab: T) => void
}

/**
 * Custom hook for managing tab navigation using URL hash
 *
 * Features:
 * - Syncs tab state with URL hash
 * - Supports back/forward browser navigation
 * - Provides two navigation methods:
 *   - navigateToTab: Creates history entry (for links/buttons)
 *   - switchToTab: Updates URL without history (for tab controls)
 *
 * @example
 * ```tsx
 * const { activeTab, navigateToTab, switchToTab } = useHashTabNavigation({
 *   hashMap: { MARKET_DATA: '', TRADE: 'trade' },
 *   defaultTab: 'MARKET_DATA'
 * })
 *
 * // From table row click - creates history entry
 * <button onClick={() => navigateToTab('TRADE')}>Trade</button>
 *
 * // From tab control - no history entry
 * <Tabs onChange={tab => switchToTab(tab)} />
 * ```
 */
export const useHashTabNavigation = <T extends string>({
  hashMap,
  defaultTab
}: UseHashTabNavigationOptions<T>): UseHashTabNavigationReturn<T> => {
  const history = useHistory()
  const location = useLocation()

  // Get tab from current hash
  const getTabFromHash = (): T => {
    const hash = location.hash.replace('#', '')
    const entry = Object.entries(hashMap).find(([, hashValue]) => hashValue === hash)
    return entry ? (entry[0] as T) : defaultTab
  }

  const [activeTab, setActiveTab] = useState<T>(getTabFromHash())

  // Navigate to tab with history entry (for Trade button clicks, links, etc.)
  const navigateToTab = (tab: T) => {
    const hash = hashMap[tab]
    const newHash = hash ? `#${hash}` : ''

    // Only push if hash is different
    if (location.hash !== newHash) {
      setActiveTab(tab)
      history.push({
        pathname: location.pathname,
        search: location.search,
        hash: newHash
      })
    }
  }

  // Switch tab without history entry (for tab button clicks)
  const switchToTab = (tab: T) => {
    const hash = hashMap[tab]
    const newHash = hash ? `#${hash}` : ''

    setActiveTab(tab)
    history.replace({
      pathname: location.pathname,
      search: location.search,
      hash: newHash
    })
  }

  // Sync activeTab with location hash changes (handles back/forward navigation)
  useEffect(() => {
    const newTab = getTabFromHash()
    if (newTab !== activeTab) {
      setActiveTab(newTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  return {
    activeTab,
    navigateToTab,
    switchToTab,
    setActiveTab
  }
}
